import React, { Component } from 'react';
import { retrieve } from './service/svgretrieve';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { combineStates, identityState, siteSettingsState } from '../store';
const NamespacedPropertyRegex = /^(.+?):(.+?)/;

const knownPropertyNames = {
  'strokewidth': 'strokeWidth',
  'fillopacity': 'fillOpacity',
  'stroke-width': 'strokeWidth',
  'fill-opacity': 'fillOpacity'
};

import Tooltip from '@mui/material/Tooltip';
import { createStyles, makeStyles } from '@material-ui/core';

const useStyles = makeStyles(() =>
  createStyles({
    tooltip: {
      background: "#fff !important",
      color: "#000 !important",
      // border: "1px solid #D9D9D9 !important",
      borderRadius: '4px !important',
      fontFamily: 'ABBvoice Regular !important',
      boxShadow: "0px 20px 32px 0px rgba(102, 102, 102, 0.32), 0px 2px 8px 0px rgba(15, 15, 15, 0.12), 0px 0px 1px 0px rgba(15, 15, 15, 0.16)",
    },
    arrow: {
      fontSize: 16,
      width: 17,
      position:'absolute',
      left:"50px",
      "&::before": {
        border: "1px solid #D9D9D9 !important",
        backgroundColor: "#fff !important",
        boxSizing: "border-box",
      }
    }
  })
);

export const LightTooltip = (props) => {
  const classes = useStyles();

  return (
    <Tooltip
      {...props}
      arrow
      classes={{
        tooltip: classes.tooltip,
        arrow: classes.arrow,
      }}
    ></Tooltip>
  );
};

const realPropertyName = input => {
  const propName = input.toLowerCase();
  return knownPropertyNames[propName] || input;
};

/*
 * @method mapAttributesToObject
 * @param {domelement} a DOMelement that contains attributes
 * @returns {object} a new object containing all relevant props
 */
function mapAttributesToObject( elem ) {
  if ( !elem || !elem.attributes ) {
    return { };
  }
  let result = { };
  for ( let propIndex in elem.attributes ) {
    let prop = elem.attributes[propIndex];
    if ( !prop.name || !prop.value || prop.name === 'style' || prop.name.match( NamespacedPropertyRegex ) ) {
      continue;
    }
    result[realPropertyName( prop.name )] = prop.value;
  }
  return result;
}

/*
 * @class SvgImage
 * Since not all browsers support Svg in the same way, an intermediary component was added for the browsers who might need it
 */
class SvgImageComponent extends Component {
  constructor( props ) {
    super( props );
    this.state = { simpleSvg: null };
    this.handleClick = this.handleClick.bind( this );
  }

  componentDidMount() {
    this.mounted = true;
    // let srcUrl = "public"+this.props.user.country+"/"+this.props.source.split("/")[1]
    retrieve( this.retrieveRealImage( this.props.source ) ).then( simpleSvg => { 
      this.mounted && this.setState( { simpleSvg } ); 
    } );
  }

  shouldComponentUpdate( nextProps, nextState ) {
    // either the image changed or the specific props changed (the props will be a new object so a simple this.props !== nextProps will always be true)
    return this.state.simpleSvg !== nextState.simpleSvg ||
      this.props.fillColor !== nextProps.fillColor ||
      this.props.resizeWidth !== nextProps.resizeWidth ||
      this.props.resizeHeight !== nextProps.resizeHeight ||
      this.props.source !== nextProps.source ||
      this.props.className !== nextProps.className ||
      this.props.toolTip !== nextProps.toolTip ||
      this.props.disabled !== nextProps.disabled;
  }

  componentDidUpdate( prevProps ) {
    if ( this.props.source !== prevProps.source ) {
      retrieve( this.retrieveRealImage( this.props.source ) ).then( simpleSvg => this.mounted && this.setState( { simpleSvg } ) );
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  retrieveRealImage( image ) {
    let { siteSettings } = this.props;
    if ( !siteSettings ) {
      return image;
    }
    let { configuration } = siteSettings;
    if ( !configuration ) {
      return image;
    }
    let { iconReplacements } = configuration;
    if ( !iconReplacements ) {
      return image;
    }
    return iconReplacements[image] || image;
  }

  handleClick( ...args ) {
    const { disabled, onClick } = this.props;
    !disabled && onClick && onClick( ...args );
  }

  renderChild( elem, key ) {
    let children;
    if ( !elem || !elem.tagName ) {
      return null;
    }
    if ( elem.tagName === 'g' ) {
      children = Array.prototype.slice.call( elem.children || elem.childNodes );
    }

    return React.createElement(
      elem.tagName,
      { key, ...mapAttributesToObject( elem ), fill: this.props.fillColor },
      !children ? null : children.map( ( child, key ) => this.renderChild( child, key ) )
    );
  }

  render( ) {
    let { simpleSvg } = this.state;
    if ( !simpleSvg ) {
      return null;
    }
    let { width, height } = simpleSvg;
    let { resizeWidth, resizeHeight, className, disabled, toolTip, hidden, isMUTooltipRequired } = this.props;

    let boundingBox = { viewBox: simpleSvg.viewBox || `0 0 ${width} ${height}` };
    boundingBox.height = resizeHeight ? resizeHeight : height;
    boundingBox.width = resizeWidth ? resizeWidth : width;
    
    let svg = <svg { ...boundingBox } className={ classNames( className, { disabled: disabled === true, hidden: hidden===true } ) } onClick={ this.handleClick }>
      { simpleSvg.elements.map( ( childElem, key ) => this.renderChild( childElem, key ) ) }
    </svg>;
    
    return toolTip ? true ?<LightTooltip key="svg-tooltip" title={toolTip}>{ svg }</LightTooltip> : <div className="svg-tooltip" title={ toolTip } data-title = {toolTip}>{ svg }</div> : svg;
  }
}

SvgImageComponent.propTypes = {
  className: PropTypes.string,
  fillColor: PropTypes.string,
  onClick: PropTypes.func,
  resizeWidth: PropTypes.string,
  resizeHeight: PropTypes.string,
  source: PropTypes.string.isRequired,
  siteSettings: PropTypes.shape( {
    configuration: PropTypes.shape( {
      iconReplacements: PropTypes.objectOf( PropTypes.string )
    } )
  } ),
  toolTip: PropTypes.string,
  disabled: PropTypes.bool,
  isMUTooltipRequired: PropTypes.bool
};

SvgImageComponent.defaultProps = {
  fillColor: '#ffffff',
  resizeWidth: '1em',
  resizeHeight: '1em'
};

export const SvgImage = connect( combineStates(siteSettingsState, identityState) )( SvgImageComponent );